import type { ScheduledJob } from "@/api/constants/jobs";
import type { IntegrationHealth } from "@/api/db/integration-health";
import { SendLabels } from "@/lib/labels";

import { OperacaoLabels } from "./operacao";
import type { ChipTone } from "./shared";
import { teamMemberLabel } from "./team";

type Integration = IntegrationHealth["integration"];
type Status = IntegrationHealth["status"];

const integrationName: Record<Integration, string> = {
	sendpulse_coletor: "Recebimento de mensagens",
	sendpulse_envio: "Envio de mensagens",
	pluggy: "Extrato bancário",
	rede: "Vendas com cartão",
	fireflies: "Transcrições de consulta",
};

const integrationSource: Record<Integration, string> = {
	sendpulse_coletor: "SendPulse",
	sendpulse_envio: "SendPulse",
	pluggy: "Pluggy",
	rede: "Rede Itaú",
	fireflies: "Fireflies",
};

const statusWord: Record<Status, string> = {
	ok: "funcionando",
	atrasado: "atrasado",
	parado: "parado",
	sem_credencial: "sem credencial",
};

const statusTone: Record<Status, ChipTone> = {
	ok: "teal",
	atrasado: "amber",
	parado: "coral",
	sem_credencial: "petrol",
};

const recovery: Record<Status, string | null> = {
	ok: null,
	atrasado: "atividade atrasada",
	parado: "atividade interrompida",
	sem_credencial: "credencial ausente",
};

const jobName: Record<string, string> = {
	collector: "Coletar conversas",
	registry: "Sincronizar cadastro",
	transcription: "Transcrever áudios",
	manuals: "Gerar manuais",
	"medico-extraction": "Extrair prontuários",
	exams: "Processar exames",
	"medico-sync": "Sincronizar transcrições",
	"bank-sync": "Sincronizar extrato bancário",
	"cards-sync": "Sincronizar vendas com cartão",
	"medico-job-watchdog": "Vigia dos jobs médicos",
	triage: "Triar conversas com IA",
	"triage-sweep": "Revisar pendências da triagem",
	"triage-curation": "Ordenar o trabalho da equipe",
};

function everyMs(ms: number) {
	const minutes = Math.round(ms / 60_000);

	if (minutes < 60) {
		return `a cada ${minutes} min`;
	}

	const hours = Math.round(minutes / 60);

	return `a cada ${hours}h`;
}

type AuditOutcome = "sent" | "simulated" | "blocked" | "pending";

const auditOutcome: Record<AuditOutcome, string> = {
	pending: "sem confirmação",
	sent: "enviada",
	simulated: "simulada",
	blocked: "bloqueada",
};

const auditOutcomeTone: Record<AuditOutcome, ChipTone> = {
	pending: "amber",
	sent: "teal",
	simulated: "petrol",
	blocked: "coral",
};

const LEGACY_AUTO_SENDER = "pode-sim-auto";

const decisionResolution: Record<string, string> = {
	check_digit: "resolvido pelo dígito verificador do CPF",
	plausibility: "ficou a data mais plausível",
	probable_typo: "corrigido um provável erro de digitação",
	both_kept: "mantidas as duas informações",
	shared_phone: "telefone compartilhado entre pessoas distintas",
	intra_source_dedup: "duplicata interna unificada",
};

const decisionField: Record<string, string> = {
	name: "nome",
	cpf: "CPF",
	phone: "telefone",
	email: "email",
	birth_date: "nascimento",
	address: "endereço",
	cep: "CEP",
	mother_name: "nome da mãe",
};

export const SystemLabels = {
	pageTitle: "Sistema",
	pageDescription: "Operações automáticas da clínica.",

	integrationName(integration: Integration) {
		return integrationName[integration];
	},
	integrationSource(integration: Integration) {
		return integrationSource[integration];
	},
	statusWord,
	statusTone,
	recovery(status: Status) {
		return recovery[status];
	},
	lastActivity(iso: Date | string | null) {
		if (iso === null) {
			return "Sem sincronização registrada.";
		}

		return `Última atividade ${OperacaoLabels.moment(iso)}.`;
	},

	jobName(name: string) {
		return jobName[name] ?? name;
	},
	cadence(job: ScheduledJob) {
		if (job.kind === "interval") {
			return everyMs(job.every_ms);
		}

		return `no minuto ${job.cron.split(" ")[0]} de cada hora`;
	},

	auditOutcome,
	auditOutcomeTone,
	auditAuthor(author: string | null) {
		if (author === null) {
			return null;
		}

		return author === LEGACY_AUTO_SENDER ? "automática" : teamMemberLabel(author);
	},
	blockReason: SendLabels.blockReason,

	decisionResolution(resolution: string) {
		return decisionResolution[resolution] ?? resolution;
	},
	decisionField(field: string) {
		return decisionField[field] ?? field;
	},

	sections: {
		integrations: "Integrações",
		sendKey: "Chave de envio",
		jobs: "Jobs agendados",
		audit: "Auditoria de envios",
		decisions: "Decisões automáticas",
		etl: "Cadastro civil",
	},

	etl: {
		hint: "Planilha de pacientes do GestãoDS",
		action: "Enviar planilha",
		neverRan: "Nenhuma carga registrada ainda.",
		lastRun(ranAt: Date | string) {
			return `Última carga ${OperacaoLabels.moment(ranAt)}`;
		},
		patients(patients: number) {
			return `${patients} pacientes na base`;
		},
		done(patients: number) {
			return `Carga concluída para ${patients} pacientes.`;
		},
		warnings(count: number) {
			return `${count} ${count === 1 ? "número fora" : "números fora"} do padrão esperado.`;
		},
		unsupported: "O arquivo precisa ser a planilha .xls ou .xlsx exportada do GestãoDS.",
		selected: "Planilha selecionada",
		confirmHint: "A carga substitui os dados civis da base de pacientes.",
		confirm: "Confirmar carga",
		confirming: "Importando...",
		cancel: "Cancelar",
		failed: "A carga falhou.",
		aborted: {
			stale_replica: "A réplica do WhatsApp está desatualizada.",
			contacts_regressed: "A base de contatos encolheu desde a última carga. Nada foi gravado.",
			invariants_violated: "A planilha produziu inconsistências. Nada foi gravado.",
		},
	},

	sendKeyOn: "Envio real ligado",
	sendKeyOnDetail: "Mensagens reais habilitadas.",
	sendKeyOff: "Modo ensaio",
	sendKeyOffDetail: "Todos os envios são simulados.",

	rehearsalBanner: "Modo ensaio. Todos os envios são simulados.",

	empty: {
		integrations: "Nenhuma integração para mostrar.",
		jobs: "Nenhum job agendado.",
		audit: "Nenhum envio registrado ainda.",
		decisions: "Nenhuma decisão automática registrada.",
	},
	loadError: "Não foi possível carregar o painel do sistema.",

	pipelineTitle: "Jobs do pipeline médico",
};
